import { createFileRoute } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { LinkBioEditorPage } from '@/components/link-bio/link-bio-editor-page';
import { parseLinkBioEditorTab, type LinkBioEditorTab } from '@/components/link-bio/link-bio-editor-sections';
import { useOrg } from '@/providers/org-provider';
import { useSalonSetupMeanings } from '@/features/salon-setup/use-salon-setup-meanings';
import { formatDocumentTitle } from '@/lib/document-title';
import { useOnlineBookingSettings } from '../settings/online-booking/hooks/use-online-booking-settings';

interface BioPageSearchParams {
  /** Open editor tab — kept in the URL so a reload lands on the same tab. */
  tab?: LinkBioEditorTab;
}

/**
 * Bio Page editor, inside the Marketing workspace so the Marketing layout gate (admin-only via
 * `canAccessMarketingPath`) runs before the editor or its queries mount.
 */
export const Route = createFileRoute('/_auth/$orgId/marketing/bio-page')({
  validateSearch: (search: Record<string, unknown>): BioPageSearchParams => ({
    tab: parseLinkBioEditorTab(search.tab),
  }),
  component: BioPageRoute,
});

function BioPageRoute() {
  const { orgId } = Route.useParams();
  // The URL carries the short code; the page, its cache and requests are keyed by the real org id.
  const { orgId: organizationId } = useOrg();
  const { tab } = Route.useSearch();
  // A salon whose bookings live on its POS books with its POS link: the Book Online card names that link.
  const { bookingsOnPos } = useSalonSetupMeanings();
  // A restaurant's button opens its ordering link, whether or not one is saved yet.
  const ordersOnline = useOnlineBookingSettings().data?.data.bookingAction === 'ORDER';
  const navigate = Route.useNavigate();

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Bio Page')}</title>
      </Helmet>

      <LinkBioEditorPage
        orgId={orgId}
        organizationId={organizationId}
        bookingsOnPos={bookingsOnPos}
        ordersOnline={ordersOnline}
        tab={tab}
        onTabChange={(next) => void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })}
      />
    </>
  );
}
